import React, {useCallback,useEffect,useRef,useState} from 'react';
import {Alert,Box,Button,Card,CardContent,LinearProgress,Typography} from '@mui/material';
import {LessonStep} from '../../types';
import {useArabicLessonData} from '../../hooks/useArabicLessonData';
import {loadAudioSource} from '../../utils/audioUtils';
import HighlightedText from '../HighlightedText';

interface AyahSearchStepProps {
  step: LessonStep;
  isLastStep?: boolean;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
}

const AyahSearchStep: React.FC<AyahSearchStepProps> = ({step,isLastStep=false,token,onNext,onBackToLetters}) => {
  const {ayahData,ayahAudioUrl,ayahReciter,ayahTranslation,ayahTafsir,loadAyah,loading,error}=useArabicLessonData(token);
  const [found,setFound]=useState<number[]>([]);
  const [showTranslation,setShowTranslation]=useState(false);
  const [showTafsir,setShowTafsir]=useState(false);
  const [audioLoading,setAudioLoading]=useState(false);
  const [audioPlaying,setAudioPlaying]=useState(false);
  const [audioPaused,setAudioPaused]=useState(false);
  const [audioError,setAudioError]=useState('');
  const pendingAudio=useRef<AbortController | null>(null);
  const activeAudio=useRef<{audio:HTMLAudioElement;url:string} | null>(null);
  const letter=typeof step.content.letter==='string'?step.content.letter:'';

  const stopAudio=useCallback((update=true)=>{
    const pending=pendingAudio.current;pendingAudio.current=null;pending?.abort();
    const active=activeAudio.current;activeAudio.current=null;
    if(active){active.audio.onended=null;active.audio.onerror=null;active.audio.pause();active.audio.src='';URL.revokeObjectURL(active.url);}
    if(update){setAudioLoading(false);setAudioPlaying(false);setAudioPaused(false);}
  },[]);

  const load=useCallback(()=>{
    stopAudio();setFound([]);setShowTranslation(false);setShowTafsir(false);setAudioError('');
    if(letter)void loadAyah(letter);
  },[letter,loadAyah,stopAudio]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>()=>stopAudio(false),[stopAudio]);

  const play=useCallback(async()=>{
    if(!ayahAudioUrl)return;
    stopAudio();setAudioError('');setAudioLoading(true);
    const request=new AbortController();pendingAudio.current=request;
    let objectUrl:string | null=null;
    try{
      objectUrl=await loadAudioSource(ayahAudioUrl,token,request.signal);
      if(pendingAudio.current!==request||request.signal.aborted)return;
      const active={audio:new Audio(objectUrl),url:objectUrl};activeAudio.current=active;objectUrl=null;
      active.audio.onended=()=>{if(activeAudio.current===active)stopAudio();};
      active.audio.onerror=()=>{if(activeAudio.current===active){setAudioError('Не удалось воспроизвести аудио аята.');stopAudio();}};
      await active.audio.play();
      if(activeAudio.current===active)setAudioPlaying(true);
    }catch{
      if(pendingAudio.current===request&&!request.signal.aborted){setAudioError('Не удалось воспроизвести аудио аята.');stopAudio();}
    }finally{
      if(objectUrl)URL.revokeObjectURL(objectUrl);
      if(pendingAudio.current===request){pendingAudio.current=null;setAudioLoading(false);}
    }
  },[ayahAudioUrl,token,stopAudio]);

  const toggleAudio=useCallback(async()=>{
    const active=activeAudio.current;
    if(!active){await play();return;}
    if(audioPlaying){active.audio.pause();setAudioPlaying(false);setAudioPaused(true);return;}
    if(audioPaused){
      try{await active.audio.play();if(activeAudio.current===active){setAudioPaused(false);setAudioPlaying(true);}}
      catch{if(activeAudio.current===active){setAudioError('Не удалось воспроизвести аудио аята.');stopAudio();}}
    }
  },[audioPlaying,audioPaused,play,stopAudio]);

  if(!letter)return <Card><CardContent><Alert severity="error">В шаге не указана буква для поиска аята.</Alert>{onBackToLetters&&<Button onClick={onBackToLetters}>К выбору буквы</Button>}</CardContent></Card>;
  if(!ayahData)return <Card><CardContent><Typography variant="h5">{step.title}</Typography>
    {loading?<><Alert severity="info">Загружаем аят из источника...</Alert><LinearProgress/></>:<>
      <Alert severity="error">{error||'Не удалось загрузить аят. Повторите попытку.'}</Alert>
      <Button onClick={load}>Повторить загрузку</Button>
      {onBackToLetters&&<Button onClick={onBackToLetters}>К выбору буквы</Button>}
    </>}
  </CardContent></Card>;

  const complete=found.length===ayahData.letter_count;
  return <Card sx={{mb:3}}><CardContent>
    <Typography variant="h5" gutterBottom>{step.title}</Typography>
    {step.content.instruction&&<Typography sx={{mb:2}}>{step.content.instruction}</Typography>}
    <Typography variant="h6">Сура {ayahData.surah_number}, аят {ayahData.ayah_number}</Typography>
    <Typography>Найдите все точные вхождения буквы «{ayahData.letter}» в тексте.</Typography>
    <Typography variant="caption">Источник: {ayahData.source}; область поиска: {ayahData.selection_scope==='full_local_numbered_corpus'?'полный установленный корпус':'выбранная сура'}.</Typography>
    {error&&<Alert severity="warning">{error}</Alert>}
    {audioError&&<Alert severity="error">{audioError}</Alert>}
    {ayahReciter&&<Typography>Чтение: {ayahReciter}</Typography>}
    <Box sx={{display:'flex',gap:1,my:2}}>
      <Button onClick={()=>void toggleAudio()} disabled={!ayahAudioUrl||audioLoading}>{audioPlaying?'Пауза':audioPaused?'Продолжить аудио':'Прослушать аят'}</Button>
      <Button onClick={()=>stopAudio()} disabled={!audioPlaying&&!audioLoading&&!audioPaused}>Остановить</Button>
      <Button onClick={load} disabled={loading}>Новый аят</Button>
    </Box>
    <LinearProgress variant="determinate" value={found.length/ayahData.letter_count*100}/>
    <Typography>Найдено: {found.length} / {ayahData.letter_count}</Typography>
    <HighlightedText text={ayahData.text_arabic} targetLetter={ayahData.letter} foundPositions={found}
      onLetterClick={index=>{if(ayahData.letter_positions.includes(index))setFound(previous=>previous.includes(index)?previous:[...previous,index]);}}
      token={token} showTooltips={true}/>
    <Box sx={{display:'flex',gap:1,my:2}}>
      <Button onClick={()=>setShowTranslation(value=>!value)}>{showTranslation?'Скрыть перевод':'Перевод'}</Button>
      <Button onClick={()=>setShowTafsir(value=>!value)}>{showTafsir?'Скрыть тафсир':'Тафсир'}</Button>
    </Box>
    {showTranslation&&<Alert severity="info">{ayahTranslation||(loading?'Перевод загружается...':'Русский перевод этого аята недоступен.')}</Alert>}
    {showTafsir&&<Alert severity="info"><Typography variant="subtitle2">Тафсир Ас-Саади, QuranEnc</Typography><Typography sx={{whiteSpace:'pre-wrap'}}>{ayahTafsir||(loading?'Тафсир загружается...':'Тафсир этого аята недоступен.')}</Typography></Alert>}
    {complete&&<Alert severity="success">Все вхождения буквы найдены в показанном тексте.</Alert>}
    {isLastStep&&<Alert severity="info">Это последний шаг: завершение сохранит вашу отметку о прохождении без оценки навыка.</Alert>}
    <Box sx={{display:'flex',gap:1,mt:2}}><Button onClick={()=>onNext()}>{isLastStep?'Завершить без упражнения':'Пропустить'}</Button><Button variant="contained" onClick={()=>onNext()}>{isLastStep?'Завершить урок':complete?'Продолжить':`Продолжить (${found.length}/${ayahData.letter_count})`}</Button></Box>
  </CardContent></Card>;
};

export default AyahSearchStep;
